/**
 * DiligenciaDocumentChecklist - Checklist de documentos por etapa da diligência.
 *
 * Exibe cada item do checklist com indicador visual de status (documento enviado
 * ou pendente), permitindo upload e exclusão de arquivos diretamente na linha.
 *
 * Sprint 4 (S4.1 + S4.2):
 * - Suporte a múltiplos arquivos por checklist key (até 10 arquivos, 25 MB cada).
 * - Suporte a items do tipo `external-form-respondi` (link + upload de respostas).
 * - Drag-and-drop nativo (sem nova dependência).
 *
 * @example
 * <DiligenciaDocumentChecklist
 *   diligenciaId="abc-123"
 *   etapa="viabilidade_juridica"
 *   checklistItems={DILIGENCIA_CHECKLISTS.viabilidade_juridica}
 *   documents={documents}
 *   onUpload={handleMultiUpload}
 *   onDelete={handleDelete}
 *   onView={handleView}
 *   isUploading={isUploading}
 * />
 */

import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  Circle,
  Upload,
  Trash2,
  FileText,
  Loader2,
  ThumbsDown,
  ThumbsUp,
  ExternalLink,
} from 'lucide-react';
import { formatFileSize } from '@/lib/upload';
import { BaseModal } from '@/components/shared/modals';
import {
  ETAPA_LABELS,
  type ChecklistItem,
  type DiligenciaEtapa,
} from '@/utils/diligenciaChecklists';
import {
  MAX_FILES_PER_CHECKLIST,
  MAX_FILE_SIZE_MB,
} from '@/features/diligencias/hooks';
import type {
  DiligenciaDocumentReviewStatus,
  MultiUploadResult,
} from '@/features/diligencias/hooks';

// =============================================================================
// TIPOS
// =============================================================================

type DocumentRow = {
  id: string;
  etapa: string;
  checklist_key: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
  review_status?: DiligenciaDocumentReviewStatus;
  review_note?: string | null;
  reviewed_at?: string | null;
};

export interface DiligenciaDocumentChecklistProps {
  /** ID da diligência associada */
  diligenciaId: string;
  /** Etapa da diligência */
  etapa: string;
  /** Itens do checklist para esta etapa */
  checklistItems: ChecklistItem[];
  /** Documentos já enviados */
  documents: DocumentRow[];
  /**
   * Callback acionado ao enviar arquivos (S4.2: agora multi-file).
   * Retorna o resultado agregado para o caller poder tratar falha parcial.
   */
  onUpload: (
    checklistKey: string,
    files: File[]
  ) => Promise<MultiUploadResult | unknown>;
  /** Callback acionado ao excluir um documento */
  onDelete: (docId: string, options?: { silent?: boolean }) => Promise<boolean>;
  /** Callback acionado ao visualizar um documento */
  onView: (filePath: string) => Promise<void>;
  /** Indica se um upload está em andamento */
  isUploading: boolean;
  /** Desabilita todas as ações de upload e exclusão */
  disabled?: boolean;
  /** Habilita revisão individual do documento */
  canReview?: boolean;
  /** Callback acionado ao aprovar ou reprovar um documento */
  onReviewStatusChange?: (
    docId: string,
    status: DiligenciaDocumentReviewStatus,
    note?: string | null
  ) => Promise<boolean>;
  /** Indica se uma revisão está em andamento */
  isReviewing?: boolean;
}

// =============================================================================
// CONSTANTES VISUAIS
// =============================================================================

const REVIEW_STATUS_LABELS: Record<DiligenciaDocumentReviewStatus, string> = {
  pendente: 'Aguardando revisão',
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
};

const REVIEW_STATUS_CLASSES: Record<DiligenciaDocumentReviewStatus, string> = {
  pendente: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  aprovado: 'bg-green-50 text-green-700 border-green-200',
  reprovado: 'bg-red-50 text-red-700 border-red-200',
};

// =============================================================================
// SUB-COMPONENTES
// =============================================================================

/**
 * Linha de arquivo já enviado, com nome, badge de revisão, ações.
 */
interface UploadedFileRowProps {
  document: DocumentRow;
  onView: (filePath: string) => Promise<void>;
  onDelete: (docId: string) => void;
  onApprove?: () => void;
  onRequestReject?: () => void;
  canReview: boolean;
  isReviewing: boolean;
  disabled: boolean;
}

function UploadedFileRow({
  document,
  onView,
  onDelete,
  onApprove,
  onRequestReject,
  canReview,
  isReviewing,
  disabled,
}: UploadedFileRowProps) {
  const reviewStatus = document.review_status ?? 'pendente';

  const handleView = async () => {
    await onView(document.file_path);
  };

  return (
    <div className="flex items-center gap-2 rounded-md border border-green-100 bg-green-50/40 px-2.5 py-1.5">
      <FileText className="h-3.5 w-3.5 shrink-0 text-green-700" />
      <button
        type="button"
        onClick={handleView}
        className="flex-1 min-w-0 text-left text-xs text-foreground hover:underline truncate"
        title={document.file_name}
      >
        {document.file_name}
        {document.file_size !== null && (
          <span className="ml-1 text-muted-foreground/70">
            ({formatFileSize(document.file_size)})
          </span>
        )}
      </button>

      <Badge
        variant="outline"
        className={cn('text-[10px] shrink-0', REVIEW_STATUS_CLASSES[reviewStatus])}
      >
        {REVIEW_STATUS_LABELS[reviewStatus]}
      </Badge>

      {canReview && onApprove && onRequestReject && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onApprove}
            disabled={isReviewing || reviewStatus === 'aprovado'}
            className="h-6 w-6 text-green-700 hover:bg-green-100"
            aria-label={`Aprovar ${document.file_name}`}
            title="Aprovar"
          >
            <ThumbsUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRequestReject}
            disabled={isReviewing || reviewStatus === 'reprovado'}
            className="h-6 w-6 text-red-700 hover:bg-red-100"
            aria-label={`Reprovar ${document.file_name}`}
            title="Reprovar"
          >
            <ThumbsDown className="h-3 w-3" />
          </Button>
        </>
      )}

      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onDelete(document.id)}
          className="h-6 w-6 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label={`Remover ${document.file_name}`}
          title="Remover"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

/**
 * Item de checklist (renderiza tanto items "document" quanto "external-form-respondi").
 */
interface ChecklistItemBlockProps {
  item: ChecklistItem;
  documents: DocumentRow[];
  onUpload: (
    checklistKey: string,
    files: File[]
  ) => Promise<MultiUploadResult | unknown>;
  onDelete: (docId: string, options?: { silent?: boolean }) => Promise<boolean>;
  onView: (filePath: string) => Promise<void>;
  isUploading: boolean;
  disabled: boolean;
  canReview: boolean;
  onReviewStatusChange?: (
    docId: string,
    status: DiligenciaDocumentReviewStatus,
    note?: string | null
  ) => Promise<boolean>;
  isReviewing: boolean;
  onRequestReject: (document: DocumentRow) => void;
}

function ChecklistItemBlock({
  item,
  documents,
  onUpload,
  onDelete,
  onView,
  isUploading,
  disabled,
  canReview,
  onReviewStatusChange,
  isReviewing,
  onRequestReject,
}: ChecklistItemBlockProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isExternal = item.kind === 'external-form-respondi';
  const fileCount = documents.length;
  const hasAnyFile = fileCount > 0;
  const atLimit = fileCount >= MAX_FILES_PER_CHECKLIST;

  /**
   * Processa lista de arquivos enviados (drop ou input change).
   * Aplica corte por limite de quantidade e delega validação ao hook.
   */
  const handleFiles = async (incoming: FileList | File[]) => {
    if (disabled || isUploading) return;

    const files = Array.from(incoming);
    if (files.length === 0) return;

    const remainingSlots = Math.max(0, MAX_FILES_PER_CHECKLIST - fileCount);
    if (remainingSlots === 0) {
      // limite atingido — caller espera toast pelo hook; aqui só interrompe.
      window.alert(
        `Limite de ${MAX_FILES_PER_CHECKLIST} arquivos atingido para "${item.label}". Remova algum arquivo antes de adicionar novos.`
      );
      return;
    }

    let toUpload = files;
    if (files.length > remainingSlots) {
      window.alert(
        `Apenas ${remainingSlots} arquivo(s) podem ser adicionado(s) (limite: ${MAX_FILES_PER_CHECKLIST}). Os primeiros ${remainingSlots} serão enviados.`
      );
      toUpload = files.slice(0, remainingSlots);
    }

    await onUpload(item.key, toUpload);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files) await handleFiles(e.target.files);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading && !atLimit) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDeleteWithToast = async (docId: string) => {
    await onDelete(docId);
  };

  const handleApprove = async (docId: string) => {
    if (!onReviewStatusChange) return;
    await onReviewStatusChange(docId, 'aprovado');
  };

  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-3 transition-colors',
        hasAnyFile
          ? 'border-green-200 bg-green-50/40 dark:bg-green-950/10'
          : 'border-muted bg-muted/30'
      )}
    >
      {/* Cabeçalho */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 pt-0.5">
          {hasAnyFile ? (
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-500" />
          ) : (
            <Circle className="h-5 w-5 text-muted-foreground/50" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-sm font-medium',
              hasAnyFile ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {item.label}
            {item.required && (
              <span className="ml-0.5 text-destructive font-medium"> *</span>
            )}
          </p>

          {isExternal && (
            <p className="mt-1 text-xs text-muted-foreground">
              Preencha o formulário externo e anexe o PDF de respostas
              exportado ao final.
            </p>
          )}

          {hasAnyFile && (
            <p className="mt-1 text-xs text-muted-foreground">
              {fileCount} de {MAX_FILES_PER_CHECKLIST} arquivo(s) anexado(s)
              · máx. {MAX_FILE_SIZE_MB} MB cada · PDF
            </p>
          )}
        </div>

        {/* Botões de ação no header */}
        <div className="shrink-0 flex items-center gap-2">
          {isExternal && item.externalUrl && (
            <a
              href={item.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium',
                'hover:bg-accent hover:text-accent-foreground transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Preencher questionário
            </a>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            className="hidden"
            onChange={handleInputChange}
            disabled={disabled || isUploading || atLimit}
            aria-label={`Adicionar arquivos para ${item.label}`}
          />

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isUploading || atLimit}
            className="h-8 gap-1.5 text-xs"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5" />
                <span>{hasAnyFile ? 'Adicionar' : 'Anexar'}</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Drag-and-drop zone (apenas quando vazio e habilitado) */}
      {!disabled && !hasAnyFile && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          className={cn(
            'mt-3 cursor-pointer rounded-md border-2 border-dashed px-3 py-4 text-center text-xs transition-colors',
            isDragging
              ? 'border-primary bg-primary/5 text-primary'
              : 'border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40'
          )}
          aria-label={`Área de arraste para ${item.label}`}
        >
          {isDragging
            ? 'Solte os arquivos aqui'
            : `Arraste PDFs aqui ou clique para selecionar (máx. ${MAX_FILES_PER_CHECKLIST} arquivos, ${MAX_FILE_SIZE_MB} MB cada)`}
        </div>
      )}

      {/* Lista de arquivos enviados */}
      {hasAnyFile && (
        <div className="mt-3 space-y-1.5">
          {documents.map((doc) => (
            <UploadedFileRow
              key={doc.id}
              document={doc}
              onView={onView}
              onDelete={handleDeleteWithToast}
              onApprove={canReview ? () => handleApprove(doc.id) : undefined}
              onRequestReject={
                canReview ? () => onRequestReject(doc) : undefined
              }
              canReview={canReview}
              isReviewing={isReviewing}
              disabled={disabled}
            />
          ))}

          {/* Notas de reprovação (uma por doc reprovado) */}
          {documents
            .filter((d) => d.review_status === 'reprovado' && d.review_note)
            .map((d) => (
              <p
                key={`note-${d.id}`}
                className="ml-2 text-[11px] text-red-700 dark:text-red-400"
              >
                Motivo da reprovação ({d.file_name}): {d.review_note}
              </p>
            ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

export function DiligenciaDocumentChecklist({
  etapa,
  checklistItems,
  documents,
  onUpload,
  onDelete,
  onView,
  isUploading,
  disabled = false,
  canReview = false,
  onReviewStatusChange,
  isReviewing = false,
}: DiligenciaDocumentChecklistProps) {
  const etapaLabel = ETAPA_LABELS[etapa as DiligenciaEtapa] ?? etapa;
  const [rejectionTarget, setRejectionTarget] =
    useState<DocumentRow | null>(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [isSubmittingRejection, setIsSubmittingRejection] = useState(false);

  /**
   * Retorna TODOS os documentos enviados para um dado checklist_key na etapa.
   */
  function getDocumentsForKey(key: string): DocumentRow[] {
    return documents.filter(
      (doc) => doc.etapa === etapa && doc.checklist_key === key
    );
  }

  const resetRejectModal = () => {
    setRejectionTarget(null);
    setRejectionNote('');
    setIsSubmittingRejection(false);
  };

  const handleRequestReject = (document: DocumentRow) => {
    setRejectionTarget(document);
    setRejectionNote(document.review_note ?? '');
  };

  const handleConfirmReject = async () => {
    if (!rejectionTarget || !onReviewStatusChange) return;

    setIsSubmittingRejection(true);
    const success = await onReviewStatusChange(
      rejectionTarget.id,
      'reprovado',
      rejectionNote
    );

    if (success) {
      resetRejectModal();
      return;
    }

    setIsSubmittingRejection(false);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Documentos — {etapaLabel}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-3">
          {checklistItems.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum item de checklist para esta etapa.
            </p>
          ) : (
            checklistItems.map((item) => (
              <ChecklistItemBlock
                key={item.key}
                item={item}
                documents={getDocumentsForKey(item.key)}
                onUpload={onUpload}
                onDelete={onDelete}
                onView={onView}
                isUploading={isUploading}
                disabled={disabled}
                canReview={canReview}
                onReviewStatusChange={onReviewStatusChange}
                isReviewing={isReviewing}
                onRequestReject={handleRequestReject}
              />
            ))
          )}

          {checklistItems.some((i) => i.required) && (
            <p className="pt-1 text-xs text-muted-foreground">
              <span className="text-destructive font-medium">*</span> Campo
              obrigatório
            </p>
          )}
        </CardContent>
      </Card>

      <BaseModal
        open={Boolean(rejectionTarget)}
        onOpenChange={(open) => {
          if (!open) resetRejectModal();
        }}
        title="Reprovar documento"
        description="Descreva o que precisa ser ajustado para orientar o próximo envio."
        variant="form"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={resetRejectModal}
              disabled={isSubmittingRejection}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmReject}
              disabled={!rejectionNote.trim() || isSubmittingRejection}
            >
              {isSubmittingRejection
                ? 'Reprovando...'
                : 'Confirmar reprovação'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Documento:{' '}
            <span className="font-medium text-foreground">
              {rejectionTarget?.file_name}
            </span>
          </p>
          <Textarea
            value={rejectionNote}
            onChange={(event) => setRejectionNote(event.target.value)}
            placeholder="Ex.: arquivo ilegível, documento incompleto ou versão desatualizada."
            className="min-h-[120px]"
            disabled={isSubmittingRejection}
          />
        </div>
      </BaseModal>
    </>
  );
}

export default DiligenciaDocumentChecklist;
